// Player settings and localStorage persistence (ui-flow §4.5, §14.1).
//
// Owner override: `style` ('classic' | 'sketch', default 'classic') picks the arena look; dark
// mode is a dark variant of either style.
//
// Parity (docs/parity/ui-qol.md §5–§6, audio.md §6, movement.md §2.2, money-mode.md §7):
// - `PARITY_TOGGLES` is the settings dialog's checkbox list in agar's order (agar's 9th, "Show
//   Quest", has no counterpart); `MORE_OPTIONS` is the "More options ▸" disclosure, whose
//   open/closed state is `moreOpen`.
// - Defaults: `showMinimap` false (agar FFA has none), `gameSounds` **true** (owner decision
//   D-A1 / exception (b): sounds on and working). Stored objects keep their values: only fresh
//   installs see changed defaults (a stored object always holds every field it was saved with).
// - Money rooms: `labelCurrency` 'auto' (the room's WELCOME default, USD) | 'USD' | 'SOL'.
// - Touch: `touchSteer` (floating joystick or follow-finger), `touchStopOnRelease` (Stop On
//   Release, default off: keep the last heading), `touchDirectionOnTouch` (reserved, movement
//   §2.2 "not in P1").
// - First-time controls hint (owner exception (c), ui-qol §9.1): its "seen" flags are their own
//   keys (`ballio.onboarding.v1`, `ballio.onboarding.money.v1`), not part of `ballio.settings`,
//   so Help ▸ "Show the controls hint again" can clear them alone: `isOnboardingSeen`,
//   `markOnboardingSeen`, `resetOnboarding`.
//
// Implemented fully (contracts step). Consumers: app.ts (owns one SettingsStore), render
// (reads Settings each frame), ui (settings modal writes through `set`), input (holdToEject,
// touch options), audio (gameSounds, menuSounds, volume).
//
// Every storage access is wrapped in try/catch: without storage the game runs on in-memory
// defaults (ui-flow §14.1).

import {
  DEFAULT_RENDER_STYLE,
  normalizeArenaSkinId,
  RENDER_STYLES,
  type RenderStyle,
} from '@ballio/shared';
import type { ApiMoneyTicket } from '../net/money-api.js';
import { type ReadonlySignal, type Signal, signal, type Unsubscribe } from '../ui/dom.js';
import { migrateSettings, SETTINGS_VERSION } from './migrate.js';

export { SETTINGS_VERSION };

/** Every localStorage key ball.io uses (ui-flow §14.1). All start with `ballio.`. */
export const STORAGE_KEYS = {
  settings: 'ballio.settings',
  /** Last nick (≤ 15 code points). */
  nick: 'ballio.nick',
  /** Equipped skin: preset id, `c:<hash>`, `local:<uuid>` or `""`. */
  skin: 'ballio.skin',
  /** Last public mode id. */
  mode: 'ballio.mode',
  /** Region id or `"auto"`. */
  region: 'ballio.region',
  /** `"1"`: hides the "Draw your own!" bubble. */
  painterOpened: 'ballio.painter.opened',
  /** `"1"`: painter first-run tips seen. */
  painterTutorialDone: 'ballio.painter.tutorialDone',
  /** `"1"`: the first-play controls hint was completed or dismissed (ui-qol §9.1). */
  onboarding: 'ballio.onboarding.v1',
  /** `"1"`: the money-room hint row (hold Q to cash out) was seen (owner exception (c)). */
  onboardingMoney: 'ballio.onboarding.money.v1',
  /** `"1"`: the one-time territory arena tip (spec §11, territory-ui.md §7) was shown. */
  onboardingArena: 'ballio.onboarding.arena.v1',
  /** Territory arena: the best max-land % per room kind (JSON `{ practice, money, free }`). */
  arenaBest: 'ballio.arena.best.v1',
  /** Last money stake picked on the stake select (USD, as a decimal string). */
  moneyStake: 'ballio.money.stake',
  /**
   * A paid money entry not played yet (JSON ticket): a reload or a refused PLAY reuses it instead
   * of buying again (it is consumed only by a spawn).
   */
  moneyTicket: 'ballio.money.ticket.v1',
} as const;

/** Graphics setting (ui-flow §4.5); 'auto' resolves to a level at runtime (render quality). */
export const QUALITY_SETTINGS = ['auto', 'sharp', 'high', 'medium', 'low', 'verylow'] as const;
export type QualitySetting = (typeof QUALITY_SETTINGS)[number];

export const TOUCH_STEER_MODES = ['joystick', 'follow'] as const;
export type TouchSteerMode = (typeof TOUCH_STEER_MODES)[number];

/**
 * Territory arena touch steering (territory-ui.md §6): a floating heading-only stick (default),
 * or "point" (the finger relative to the screen centre).
 */
export const ARENA_STEER_MODES = ['stick', 'point'] as const;
export type ArenaSteerMode = (typeof ARENA_STEER_MODES)[number];

/** Money label unit (money-mode §7): 'auto' = the room's WELCOME default (USD unless set). */
export const LABEL_CURRENCY_SETTINGS = ['auto', 'USD', 'SOL'] as const;
export type LabelCurrencySetting = (typeof LABEL_CURRENCY_SETTINGS)[number];

/** The `ballio.settings` object (ui-flow §14.1 plus the owner's `style`). */
export interface Settings {
  readonly v: typeof SETTINGS_VERSION;
  readonly quality: QualitySetting;
  /** Arena look: 'classic' (default, agar-like) or 'sketch' (paper and boiling ink). */
  readonly style: RenderStyle;
  readonly hideSkins: boolean;
  readonly hideNames: boolean;
  readonly plainColors: boolean;
  readonly showMass: boolean;
  readonly darkMode: boolean;
  readonly skipSummary: boolean;
  readonly showMinimap: boolean;
  readonly minimapPlayers: boolean;
  readonly minimapCollapsed: boolean;
  readonly showStats: boolean;
  readonly holdToEject: boolean;
  readonly gameSounds: boolean;
  readonly menuSounds: boolean;
  /** Master volume 0–1. */
  readonly volume: number;
  /** null = follow `prefers-reduced-motion`. */
  readonly reduceMotion: boolean | null;
  readonly touchSteer: TouchSteerMode;
  /** Stop On Release (movement §2.2, §4.3): off = keep the last heading and speed. */
  readonly touchStopOnRelease: boolean;
  /** Direction on Touch (movement §2.2): reserved, not wired in P1. */
  readonly touchDirectionOnTouch: boolean;
  /** Money rooms: label unit for values (cells, leaderboard, HUD). */
  readonly labelCurrency: LabelCurrencySetting;
  /** The settings dialog's "More options ▸" disclosure is open (ui-qol §5). */
  readonly moreOpen: boolean;
  /** Territory arena: touch steering style (territory-ui.md §6, §7). */
  readonly arenaSteer: ArenaSteerMode;
  /** Territory arena: the minimap shows everyone's land, not only your own (UI §3). */
  readonly arenaMinimapAll: boolean;
  /** Territory arena: a sound when an enemy head enters your land (audio row 7, default off). */
  readonly arenaAlarm: boolean;
  /**
   * Territory arena: the picked skin (`t:<slug>` from the shared catalogue, `""` = none; SK-1).
   * Sent in PLAY to arena rooms; the picker on the territory home sets it.
   */
  readonly arenaSkin: string;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  v: SETTINGS_VERSION,
  quality: 'auto',
  style: DEFAULT_RENDER_STYLE,
  hideSkins: false,
  hideNames: false,
  plainColors: false,
  showMass: false,
  darkMode: false,
  skipSummary: false,
  showMinimap: false,
  minimapPlayers: false,
  minimapCollapsed: false,
  showStats: false,
  holdToEject: false,
  gameSounds: true,
  menuSounds: true,
  volume: 0.7,
  reduceMotion: null,
  touchSteer: 'joystick',
  touchStopOnRelease: false,
  touchDirectionOnTouch: false,
  labelCurrency: 'auto',
  moreOpen: false,
  arenaSteer: 'stick',
  arenaMinimapAll: false,
  arenaAlarm: false,
  arenaSkin: '',
});

/** Boolean settings keys. */
export type BoolSettingKey = {
  [K in keyof Settings]: Settings[K] extends boolean ? K : never;
}[keyof Settings];

/**
 * The settings dialog's checkboxes in agar's order, laid out row-major in two columns
 * (ui-qol §5): Hide skins | Hide names / Plain colors | Dark mode / Show mass | Skip round
 * summary / Game sounds | Menu sounds. Toggling them is silent (audio §3 row 8).
 */
export const PARITY_TOGGLES: readonly BoolSettingKey[] = [
  'hideSkins',
  'hideNames',
  'plainColors',
  'darkMode',
  'showMass',
  'skipSummary',
  'gameSounds',
  'menuSounds',
];

/**
 * "More options ▸" (ui-qol §5), in order. Booleans are checkboxes; `volume` a 0–100 % slider;
 * `reduceMotion` a tri-state (null = follow the system); `style`, `touchSteer` and
 * `labelCurrency` selects; `quality` stays in the dialog's main Graphics select.
 */
export const MORE_OPTIONS: readonly (keyof Omit<Settings, 'v'>)[] = [
  'showMinimap',
  'minimapPlayers',
  'showStats',
  'holdToEject',
  'volume',
  'reduceMotion',
  'style',
  'labelCurrency',
  'touchSteer',
  'touchStopOnRelease',
  'arenaMinimapAll',
  'arenaAlarm',
  'arenaSteer',
];

// ---------------------------------------------------------------------------------------------
// Storage access
// ---------------------------------------------------------------------------------------------

/** The part of the Web Storage API used here (tests pass a fake). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `globalThis.localStorage`, or null when it is missing or access throws (privacy modes). */
export function defaultStorage(): StorageLike | null {
  try {
    const s = (globalThis as { localStorage?: StorageLike }).localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

export function readKey(
  key: string,
  storage: StorageLike | null = defaultStorage(),
): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function writeKey(
  key: string,
  value: string,
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Quota or access errors: keep running on memory only.
  }
}

export function removeKey(key: string, storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------------------------
// Normalising and persisting
// ---------------------------------------------------------------------------------------------

const BOOL_KEYS: readonly BoolSettingKey[] = [
  'hideSkins',
  'hideNames',
  'plainColors',
  'showMass',
  'darkMode',
  'skipSummary',
  'showMinimap',
  'minimapPlayers',
  'minimapCollapsed',
  'showStats',
  'holdToEject',
  'gameSounds',
  'menuSounds',
  'touchStopOnRelease',
  'touchDirectionOnTouch',
  'moreOpen',
  'arenaMinimapAll',
  'arenaAlarm',
];

function oneOf<T extends string>(list: readonly T[], v: unknown, fallback: T): T {
  return typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : fallback;
}

/** Any value → valid Settings: each invalid, unknown or missing field takes its default. */
export function normalizeSettings(raw: unknown): Settings {
  const src: Record<string, unknown> =
    raw !== null && typeof raw === 'object' && !Array.isArray(raw)
      ? migrateSettings(raw as Record<string, unknown>)
      : {};
  const d = DEFAULT_SETTINGS;
  const out: { -readonly [K in keyof Settings]: Settings[K] } = { ...d };
  out.quality = oneOf(QUALITY_SETTINGS, src['quality'], d.quality);
  out.style = oneOf(RENDER_STYLES, src['style'], d.style);
  for (const k of BOOL_KEYS) {
    const v = src[k];
    if (typeof v === 'boolean') out[k] = v;
  }
  const vol = src['volume'];
  out.volume =
    typeof vol === 'number' && Number.isFinite(vol) ? Math.min(1, Math.max(0, vol)) : d.volume;
  const rm = src['reduceMotion'];
  out.reduceMotion = rm === null || typeof rm === 'boolean' ? rm : d.reduceMotion;
  out.touchSteer = oneOf(TOUCH_STEER_MODES, src['touchSteer'], d.touchSteer);
  out.labelCurrency = oneOf(LABEL_CURRENCY_SETTINGS, src['labelCurrency'], d.labelCurrency);
  out.arenaSteer = oneOf(ARENA_STEER_MODES, src['arenaSteer'], d.arenaSteer);
  // An id this build doesn't know (a retired skin, garbage) falls back to no skin.
  out.arenaSkin = normalizeArenaSkinId(src['arenaSkin']);
  return out;
}

/** Reads `ballio.settings` (defaults when missing, unparsable or storage is unavailable). */
export function loadSettings(storage: StorageLike | null = defaultStorage()): Settings {
  const text = readKey(STORAGE_KEYS.settings, storage);
  if (text === null) return normalizeSettings(null);
  try {
    return normalizeSettings(JSON.parse(text));
  } catch {
    return normalizeSettings(null);
  }
}

export function saveSettings(
  settings: Settings,
  storage: StorageLike | null = defaultStorage(),
): void {
  writeKey(STORAGE_KEYS.settings, JSON.stringify(settings), storage);
}

function settingsEqual(a: Settings, b: Settings): boolean {
  for (const k of Object.keys(a) as (keyof Settings)[]) if (a[k] !== b[k]) return false;
  return true;
}

/**
 * The live settings: a signal, saved to `ballio.settings` on every change (ui-flow §4.5: every
 * change applies at once).
 */
export class SettingsStore {
  readonly state: ReadonlySignal<Settings>;
  private readonly sig: Signal<Settings>;
  private readonly storage: StorageLike | null;

  constructor(storage: StorageLike | null = defaultStorage()) {
    this.storage = storage;
    this.sig = signal<Settings>(loadSettings(storage), settingsEqual);
    this.state = this.sig;
  }

  get(): Settings {
    return this.sig.get();
  }

  set<K extends keyof Omit<Settings, 'v'>>(key: K, value: Settings[K]): void {
    this.update({ [key]: value } as Partial<Settings>);
  }

  /** Applies a patch (validated like a stored object) and saves it. */
  update(patch: Partial<Settings>): void {
    const next = normalizeSettings({ ...this.sig.get(), ...patch });
    if (settingsEqual(next, this.sig.get())) return;
    this.sig.set(next);
    saveSettings(next, this.storage);
  }

  subscribe(fn: (value: Settings, previous: Settings) => void): Unsubscribe {
    return this.sig.subscribe(fn);
  }
}

/**
 * Whether motion effects are off: the explicit setting, or `prefers-reduced-motion` when it is
 * null. `matchMedia` defaults to the browser's (false when absent).
 */
export function resolveReduceMotion(
  settings: Settings,
  matchMedia: ((query: string) => { matches: boolean }) | null = typeof globalThis.matchMedia ===
  'function'
    ? (q) => globalThis.matchMedia(q)
    : null,
): boolean {
  if (settings.reduceMotion !== null) return settings.reduceMotion;
  try {
    return matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

// --- the other `ballio.*` keys ----------------------------------------------------------------

export function loadString(
  key: string,
  fallback = '',
  storage: StorageLike | null = defaultStorage(),
): string {
  return readKey(key, storage) ?? fallback;
}

export function saveString(
  key: string,
  value: string,
  storage: StorageLike | null = defaultStorage(),
): void {
  writeKey(key, value, storage);
}

// --- first-time hints (ui-qol §9.1) -----------------------------------------------------------

/**
 * Which first-time hint: the controls rows, the money-room cash-out row, or the one-time tip of
 * the first territory arena life (spec §11).
 */
export type OnboardingKind = 'controls' | 'money' | 'arena';

function onboardingKey(kind: OnboardingKind): string {
  return kind === 'money'
    ? STORAGE_KEYS.onboardingMoney
    : kind === 'arena'
      ? STORAGE_KEYS.onboardingArena
      : STORAGE_KEYS.onboarding;
}

/**
 * Whether the hint was seen (stored `"1"`). False when storage is unavailable: the hint then
 * shows once per page load (the hint component keeps that in memory, ui-qol §9.1).
 */
export function isOnboardingSeen(
  kind: OnboardingKind,
  storage: StorageLike | null = defaultStorage(),
): boolean {
  return readKey(onboardingKey(kind), storage) === '1';
}

/** Stores `"1"` (on completion, on ×, or after ≥ 5 s visible; ui-qol §9.1). */
export function markOnboardingSeen(
  kind: OnboardingKind,
  storage: StorageLike | null = defaultStorage(),
): void {
  writeKey(onboardingKey(kind), '1', storage);
}

/** Help ▸ "Show the controls hint again" (ui-qol §9.3): clears both flags. */
export function resetOnboarding(storage: StorageLike | null = defaultStorage()): void {
  removeKey(STORAGE_KEYS.onboarding, storage);
  removeKey(STORAGE_KEYS.onboardingMoney, storage);
  // The retired backstab tip's flag (the agar.io money game, removed 2026-10-31).
  removeKey('ballio.onboarding.backstab.v1', storage);
  removeKey(STORAGE_KEYS.onboardingArena, storage);
}

// --- territory arena best (territory-ui.md §2.1) ------------------------------------------------

export type ArenaRoomKind = 'practice' | 'money' | 'free';

/** The best max-land % stored for a room kind, or null (none, or a stored 0). */
export function loadArenaBest(
  kind: ArenaRoomKind,
  storage: StorageLike | null = defaultStorage(),
): number | null {
  const text = readKey(STORAGE_KEYS.arenaBest, storage);
  if (text === null) return null;
  try {
    const v = (JSON.parse(text) as Record<string, unknown> | null)?.[kind];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Stores `pct` as the best for `kind` when it beats the stored one; returns the best. */
export function saveArenaBest(
  kind: ArenaRoomKind,
  pct: number,
  storage: StorageLike | null = defaultStorage(),
): number | null {
  const prev = loadArenaBest(kind, storage);
  if (!(pct > 0) || (prev !== null && prev >= pct)) return prev;
  let all: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(readKey(STORAGE_KEYS.arenaBest, storage) ?? '{}') as unknown;
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      all = parsed as Record<string, unknown>;
    }
  } catch {
    all = {};
  }
  all[kind] = Math.round(pct * 100) / 100;
  writeKey(STORAGE_KEYS.arenaBest, JSON.stringify(all), storage);
  return pct;
}

// --- money ------------------------------------------------------------------------------------
// The account is the signed-in one (auth/session.ts); no account id is stored here any more.

/** The last stake picked (USD), or null. */
export function loadMoneyStake(storage: StorageLike | null = defaultStorage()): number | null {
  const v = Number(readKey(STORAGE_KEYS.moneyStake, storage) ?? '');
  return Number.isFinite(v) && v > 0 ? v : null;
}

export function saveMoneyStake(
  stakeUsd: number,
  storage: StorageLike | null = defaultStorage(),
): void {
  if (Number.isFinite(stakeUsd) && stakeUsd > 0) {
    writeKey(STORAGE_KEYS.moneyStake, String(stakeUsd), storage);
  }
}

/**
 * The unused paid entry kept for `account` at `stakeUsd` (`ballio.money.ticket.v1`), or null. Only
 * one is kept: a ticket is consumed by the spawn it pays for, so a new buy-in replaces the old one
 * only after that one was used or refused as used.
 */
export function loadMoneyTicket(
  account: string,
  stakeUsd: number,
  storage: StorageLike | null = defaultStorage(),
): ApiMoneyTicket | null {
  const text = readKey(STORAGE_KEYS.moneyTicket, storage);
  if (text === null) return null;
  try {
    const t = JSON.parse(text) as Partial<ApiMoneyTicket> | null;
    if (
      t === null ||
      typeof t !== 'object' ||
      typeof t.id !== 'string' ||
      t.id === '' ||
      t.account !== account ||
      t.stakeUsd !== stakeUsd
    ) {
      return null;
    }
    return {
      id: t.id,
      account: t.account,
      stakeUsd: t.stakeUsd,
      lamportsPaid: typeof t.lamportsPaid === 'number' ? t.lamportsPaid : 0,
      solUsd: typeof t.solUsd === 'number' ? t.solUsd : 0,
      issuedAt: typeof t.issuedAt === 'number' ? t.issuedAt : 0,
    };
  } catch {
    return null;
  }
}

export function saveMoneyTicket(
  ticket: ApiMoneyTicket,
  storage: StorageLike | null = defaultStorage(),
): void {
  writeKey(STORAGE_KEYS.moneyTicket, JSON.stringify(ticket), storage);
}

/** Forget the kept entry (it was used by a spawn, or the server says it cannot be used). */
export function clearMoneyTicket(storage: StorageLike | null = defaultStorage()): void {
  removeKey(STORAGE_KEYS.moneyTicket, storage);
}
